/*
 * Nursem scroll-indicator dots (v2: lazy and light).
 * Auto-wires pagination dots to every horizontal-scroll section
 * (any `.nursem-scroll-container` or `[data-scroll-dots]` with a `*__track`
 * element, or Horizon's <slideshow-slides>). No per-section markup needed.
 *
 * Dots only appear when the track actually overflows, i.e. on mobile where the
 * card rows scroll. On tablet+ the tracks become grids, so the dots stay hidden
 * (also enforced in CSS). Markup and CSS are unchanged from v1:
 * .nursem-scroll-dots / .nursem-scroll-dots__dot, styled in
 * snippets/nursem-header-styles.liquid. The tracks keep CSS scroll-snap.
 *
 * Why v2 (audit finding 01-02): v1 initialised EVERY carousel at page load and
 * attached a ResizeObserver per track. Each build() forced a synchronous layout
 * read, and the observers re-ran it on every late image load, costing ~0.7s of
 * main-thread time on a mobile load. v2 keeps the exact same behaviour but:
 *  - wires a carousel only when it comes near the viewport (IntersectionObserver),
 *    so below-the-fold sections cost nothing at load
 *  - defers each build to idle time so it never competes with first paint / taps
 *  - measures once per build and rebuilds only when geometry can really change
 *    (debounced window resize, the track's images finishing load, or async content
 *    being injected). No per-track ResizeObserver storm.
 *  - uses one delegated click handler per dots rail instead of one per dot
 */
(function () {
  'use strict';

  var SELECTOR = '.nursem-scroll-container, [data-scroll-dots]';

  var idle =
    window.requestIdleCallback ||
    function (cb) {
      return setTimeout(function () {
        cb({ didTimeout: false, timeRemaining: function () { return 0; } });
      }, 1);
    };

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  function initDots(container) {
    // Re-initialise only if a previous dots element was dropped (theme-editor
    // re-render / hot reload morphing the DOM out from under us).
    if (container.__nursemDots && container.__nursemDots.isConnected) return;
    var track = container.querySelector('[class*="__track"], slideshow-slides');
    if (!track) return;

    var dotsEl = document.createElement('div');
    dotsEl.className = 'nursem-scroll-dots';
    dotsEl.setAttribute('role', 'tablist');
    container.appendChild(dotsEl);
    container.__nursemDots = dotsEl;

    var dots = [];
    var maxScroll = 0;

    function build() {
      // One measurement per build (this is the only forced layout read).
      var cw = track.clientWidth;
      var sw = track.scrollWidth;
      maxScroll = sw - cw;
      // Round up so a partial trailing item still gets its own dot.
      var pages = cw ? Math.ceil((sw - 2) / cw) : 0;
      var visible = pages > 1;
      dotsEl.classList.toggle('nursem-scroll-dots--visible', visible);
      if (!visible) {
        dotsEl.innerHTML = '';
        dots = [];
        return;
      }
      if (dots.length !== pages) {
        dotsEl.innerHTML = '';
        dots = [];
        for (var i = 0; i < pages; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'nursem-scroll-dots__dot';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.dataset.index = String(i);
          dotsEl.appendChild(b);
          dots.push(b);
        }
      }
      update();
    }

    // One delegated click handler for the whole rail.
    dotsEl.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('.nursem-scroll-dots__dot') : null;
      if (!b) return;
      var idx = parseInt(b.dataset.index, 10) || 0;
      var left = dots.length > 1 ? (idx / (dots.length - 1)) * maxScroll : 0;
      track.scrollTo({ left: left, behavior: 'smooth' });
    });

    function update() {
      if (!dots.length) return;
      // Map the full scroll range onto the dots so the first dot is active at the
      // start and the LAST dot at the very end (plain scrollLeft/clientWidth never
      // reaches the final dot).
      var active = maxScroll > 0 ? Math.round((track.scrollLeft / maxScroll) * (dots.length - 1)) : 0;
      if (active < 0) active = 0;
      if (active > dots.length - 1) active = dots.length - 1;
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === active);
      }
    }

    var raf;
    track.addEventListener(
      'scroll',
      function () {
        if (raf) return;
        raf = window.requestAnimationFrame(function () {
          raf = null;
          update();
        });
      },
      { passive: true }
    );

    // Rebuild only when geometry can actually change.
    var rebuild = debounce(build, 150);
    window.addEventListener('resize', rebuild, { passive: true });
    // Late-loading images widen the track: one rebuild once they land.
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', rebuild, { once: true });
    });
    // Async content injected later (e.g. product recommendations).
    if ('MutationObserver' in window) {
      new MutationObserver(rebuild).observe(track, { childList: true });
    }

    build();
  }

  // Lazy: wire a carousel only once it comes within 200px of the viewport,
  // and do the work in idle time.
  var lazy =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (en) {
              if (!en.isIntersecting) return;
              lazy.unobserve(en.target);
              idle(function () {
                initDots(en.target);
              });
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  function observeAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(function (c) {
      if (c.__nursemDots && c.__nursemDots.isConnected) return;
      if (lazy) lazy.observe(c);
      else initDots(c);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observeAll(); });
  } else {
    observeAll();
  }

  // Re-wire dots when a section is re-rendered in the theme editor (and on hot
  // reload), so the dots don't vanish after a customizer edit.
  document.addEventListener('shopify:section:load', function (event) {
    var sec = event.target;
    if (!sec || !sec.querySelectorAll) return;
    if (sec.matches && sec.matches(SELECTOR)) initDots(sec);
    observeAll(sec);
  });
})();
